import * as React from 'react';
import { ValidationError } from '@oysterjs/core/graphql';
import { getValidationError } from '@oysterjs/core/graphql/form';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Select } from '@oysterjs/ui/select';
import { Switch, SwitchField } from '@oysterjs/ui/switch';
import { AutoResizeTextarea, Textarea } from '@oysterjs/ui/textarea';
import {
  useGetOutboundCallFrequencyQuery,
  VoiceAgentOverviewFieldsFragment
} from '../../api/graphql';
import { FEATURE_FLAGS, useFlag } from '@oysterjs/ais/components/feature-flags';
import {
  convertScheduledAtBetweenTimezones,
  defaultScheduledTimezone,
  formatOutboundCallFrequencyDescription,
  OUTBOUND_CALL_FREQUENCY_TEASER,
  OUTBOUND_CALL_TIMEZONES,
  outboundCallTimezoneOptions,
  OutboundCallDraft,
  resolveVoiceAgentCallbackPhonePlaceholder
} from './outbound-call';

export type OutboundCallFormFieldsProps = {
  draft: OutboundCallDraft;
  agents: VoiceAgentOverviewFieldsFragment[];
  onDraftChange: (patch: Partial<OutboundCallDraft>) => void;
  validationErrors?: ValidationError[];
  showSchedule?: boolean;
  /**
   * `sections` — Calls create/edit dialog with side headings.
   * `compact` — Copilot artifact card (single column, denser fields).
   */
  layout?: 'sections' | 'compact';
  /** When true, agent select shows a loading / empty placeholder option. */
  agentsLoading?: boolean;
  /** When editing, exclude this job from the frequency count so it does not count against itself. */
  excludeOutboundCallId?: string;
};

const AgentSelect = ({
  draft,
  agents,
  onDraftChange,
  agentsLoading,
  allowEmpty,
  validationErrors
}: {
  draft: OutboundCallDraft;
  agents: VoiceAgentOverviewFieldsFragment[];
  onDraftChange: (patch: Partial<OutboundCallDraft>) => void;
  agentsLoading?: boolean;
  allowEmpty: boolean;
  validationErrors: ValidationError[];
}) => {
  const error = getValidationError(validationErrors, ['voiceAgentId']);
  return (
    <Field>
      <Label>Outbound voice agent</Label>
      <Select
        value={draft.voiceAgentId}
        onChange={(e) => onDraftChange({ voiceAgentId: e.currentTarget.value })}
        disabled={agentsLoading}
        invalid={!!error}
      >
        {allowEmpty && (
          <option value="">{agentsLoading ? 'Loading agents...' : '— Select an agent —'}</option>
        )}
        {agents.map((agent) => (
          <option key={agent.id} value={agent.id}>
            {agent.name}
            {agent.phoneNumber ? ` (${agent.phoneNumber})` : ''}
          </option>
        ))}
      </Select>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Field>
  );
};

const ScheduleFields = ({
  draft,
  onDraftChange,
  validationErrors
}: {
  draft: OutboundCallDraft;
  onDraftChange: (patch: Partial<OutboundCallDraft>) => void;
  validationErrors: ValidationError[];
}) => {
  const scheduleLaterError = getValidationError(validationErrors, ['schedule', 'scheduleLater']);
  const scheduledAtError = getValidationError(validationErrors, ['schedule', 'scheduledAt']);
  const timezoneError = getValidationError(validationErrors, ['schedule', 'timezone']);
  const timezoneOptions = outboundCallTimezoneOptions(draft.scheduledTimezone);
  return (
    <>
      <SwitchField>
        <Label>Schedule for later</Label>
        <Description>
          When off, Cara places the call as soon as you submit. When on, choose a date and time.
        </Description>
        <Switch
          color="sky"
          checked={draft.scheduleLater}
          onChange={(checked) =>
            onDraftChange({
              scheduleLater: checked,
              ...(checked ? {} : { scheduledAt: '' })
            })
          }
        />
      </SwitchField>
      {scheduleLaterError && <ErrorMessage>{scheduleLaterError}</ErrorMessage>}
      {draft.scheduleLater && (
        <Field>
          <Label>Schedule call</Label>
          <Description>
            Enter the date and time in the selected timezone. Defaults to{' '}
            {OUTBOUND_CALL_TIMEZONES.find((tz) => tz.value === defaultScheduledTimezone())?.label ??
              'your timezone'}
            . Calls must be between 8:00 AM and 8:00 PM in that timezone.
          </Description>
          <div data-slot="control" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div data-slot="control">
              <Input
                type="datetime-local"
                value={draft.scheduledAt}
                onChange={(e) => onDraftChange({ scheduledAt: e.currentTarget.value })}
                invalid={!!scheduledAtError}
              />
            </div>
            <div data-slot="control">
              <Select
                value={draft.scheduledTimezone}
                onChange={(e) => {
                  const nextTimezone = e.currentTarget.value;
                  onDraftChange({
                    scheduledTimezone: nextTimezone,
                    scheduledAt: convertScheduledAtBetweenTimezones(
                      draft.scheduledAt,
                      draft.scheduledTimezone,
                      nextTimezone
                    )
                  });
                }}
                invalid={!!timezoneError}
              >
                {timezoneOptions.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </Select>
            </div>
            {scheduledAtError && <ErrorMessage>{scheduledAtError}</ErrorMessage>}
            {timezoneError && <ErrorMessage>{timezoneError}</ErrorMessage>}
          </div>
        </Field>
      )}
    </>
  );
};

const VoicemailSwitch = ({
  draft,
  onDraftChange
}: {
  draft: OutboundCallDraft;
  onDraftChange: (patch: Partial<OutboundCallDraft>) => void;
}) => (
  <SwitchField>
    <Label>Leave voicemail if no answer</Label>
    <Description>If the customer doesn't answer, Cara will leave a voicemail.</Description>
    <Switch
      color="sky"
      checked={draft.leaveVoicemail}
      onChange={(checked) => onDraftChange({ leaveVoicemail: checked })}
    />
  </SwitchField>
);

const CustomerFields = ({
  draft,
  onDraftChange,
  validationErrors,
  excludeOutboundCallId
}: {
  draft: OutboundCallDraft;
  onDraftChange: (patch: Partial<OutboundCallDraft>) => void;
  validationErrors: ValidationError[];
  excludeOutboundCallId?: string;
}) => {
  const nameError = getValidationError(validationErrors, ['customer', 'name']);
  const phoneError = getValidationError(validationErrors, ['customer', 'phone']);
  const frequencyCapEnabled = useFlag(FEATURE_FLAGS.OUTBOUND_CALL_FREQUENCY_CAP);
  const [debouncedPhone, setDebouncedPhone] = React.useState(draft.customerPhone.trim());

  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedPhone(draft.customerPhone.trim());
    }, 300);
    return () => window.clearTimeout(handle);
  }, [draft.customerPhone]);

  // Digits-only length gate avoids querying while the user is still typing a US number.
  const phoneReady = frequencyCapEnabled && debouncedPhone.replace(/\D/g, '').length >= 10;
  const {
    data: frequencyData,
    loading: frequencyLoading,
    error: frequencyError
  } = useGetOutboundCallFrequencyQuery({
    variables: {
      phone: debouncedPhone,
      excludeOutboundCallId: excludeOutboundCallId || null
    },
    skip: !phoneReady,
    fetchPolicy: 'cache-and-network'
  });
  const frequency = frequencyData?.outboundCallFrequency;
  // Teaser while the number is incomplete; hide during load/error so we don't flash stale copy.
  const showFrequencyTeaser =
    frequencyCapEnabled && !frequency && !frequencyLoading && !frequencyError;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <Field>
        <Label>Full name</Label>
        <Input
          value={draft.customerName}
          onChange={(e) => onDraftChange({ customerName: e.currentTarget.value })}
          placeholder="Jane Smith"
          invalid={!!nameError}
        />
        {nameError && <ErrorMessage>{nameError}</ErrorMessage>}
      </Field>
      <Field>
        <Label>Phone number</Label>
        <Input
          type="tel"
          value={draft.customerPhone}
          onChange={(e) => onDraftChange({ customerPhone: e.currentTarget.value })}
          placeholder="+1 (555) 234-5678"
          invalid={!!phoneError}
        />
        {phoneError && <ErrorMessage>{phoneError}</ErrorMessage>}
        {frequencyCapEnabled && frequency && (
          <Description
            className={frequency.remaining === 0 ? 'text-amber-700 dark:text-amber-300' : undefined}
          >
            {formatOutboundCallFrequencyDescription(
              frequency.count,
              frequency.limit,
              frequency.windowDays
            )}
          </Description>
        )}
        {showFrequencyTeaser && <Description>{OUTBOUND_CALL_FREQUENCY_TEASER}</Description>}
      </Field>
    </div>
  );
};

const MessageFields = ({
  draft,
  onDraftChange,
  compact,
  validationErrors,
  callbackPhonePlaceholder = '+1 (555) 123-4567'
}: {
  draft: OutboundCallDraft;
  onDraftChange: (patch: Partial<OutboundCallDraft>) => void;
  compact: boolean;
  validationErrors: ValidationError[];
  callbackPhonePlaceholder?: string;
}) => {
  const callObjectiveError = getValidationError(validationErrors, ['callConfig', 'callObjective']);
  return (
    <>
      <Field>
        <Label>Call objective</Label>
        <Description>What Cara should say or accomplish on this call.</Description>
        <AutoResizeTextarea
          rows={compact ? 3 : 4}
          style={{ maxHeight: compact ? '240px' : undefined }}
          resizable={!compact}
          value={draft.callObjective}
          onChange={(e) => onDraftChange({ callObjective: e.currentTarget.value })}
          placeholder={
            compact
              ? 'What should Cara accomplish on this call?'
              : 'Remind Ashley her renewal packet arrives this week.'
          }
          invalid={!!callObjectiveError}
        />
        {callObjectiveError && <ErrorMessage>{callObjectiveError}</ErrorMessage>}
      </Field>
      <Field>
        <Label>Customer context (optional)</Label>
        <Description>
          Facts Cara can reference on the call. Only this information can be shared.
        </Description>
        <AutoResizeTextarea
          rows={compact ? 3 : 4}
          style={{ maxHeight: compact ? '240px' : undefined }}
          resizable={!compact}
          value={draft.customerContext}
          onChange={(e) => onDraftChange({ customerContext: e.currentTarget.value })}
          placeholder={
            compact
              ? 'Account details, policy info, prior conversations…'
              : 'Policy: CA-12345\nAmount due: $1,247.00\nDue date: March 1, 2026'
          }
        />
      </Field>
      <Field>
        <Label>Guidelines (optional)</Label>
        <Description>Optional instructions for how Cara should handle the call.</Description>
        <Textarea
          rows={2}
          resizable={!compact}
          value={draft.guidelines}
          onChange={(e) => onDraftChange({ guidelines: e.currentTarget.value })}
          placeholder="Keep the call short and to the point. Mention they can call our office with questions."
        />
      </Field>
      <Field>
        <Label>Callback number (optional)</Label>
        <Description>
          Phone number Cara should give if the customer needs to call back. Leave blank to use the
          agency phone from voice settings.
        </Description>
        <Input
          type="tel"
          value={draft.callbackPhone}
          onChange={(e) => onDraftChange({ callbackPhone: e.currentTarget.value })}
          placeholder={callbackPhonePlaceholder}
          invalid={!!getValidationError(validationErrors, ['callConfig', 'callbackPhone'])}
        />
        {getValidationError(validationErrors, ['callConfig', 'callbackPhone']) && (
          <ErrorMessage>
            {getValidationError(validationErrors, ['callConfig', 'callbackPhone'])}
          </ErrorMessage>
        )}
      </Field>
    </>
  );
};

/**
 * Shared outbound-call draft fields for the Calls create/edit dialog and Copilot
 * draft artifact card.
 */
export const OutboundCallFormArtifactFields = ({
  draft,
  agents,
  onDraftChange,
  validationErrors = [],
  showSchedule = false,
  agentsLoading = false,
  excludeOutboundCallId
}: OutboundCallFormFieldsProps) => {
  const selectedAgent = agents.find((agent) => agent.id === draft.voiceAgentId) ?? agents[0];
  const callbackPhonePlaceholder = resolveVoiceAgentCallbackPhonePlaceholder(selectedAgent);
  return (
    <FieldGroup>
      <AgentSelect
        draft={draft}
        agents={agents}
        onDraftChange={onDraftChange}
        agentsLoading={agentsLoading}
        allowEmpty
        validationErrors={validationErrors}
      />
      <CustomerFields
        draft={draft}
        onDraftChange={onDraftChange}
        validationErrors={validationErrors}
        excludeOutboundCallId={excludeOutboundCallId}
      />
      <MessageFields
        draft={draft}
        onDraftChange={onDraftChange}
        compact
        validationErrors={validationErrors}
        callbackPhonePlaceholder={callbackPhonePlaceholder}
      />
      {showSchedule && (
        <ScheduleFields
          draft={draft}
          onDraftChange={onDraftChange}
          validationErrors={validationErrors}
        />
      )}
      <VoicemailSwitch draft={draft} onDraftChange={onDraftChange} />
    </FieldGroup>
  );
};

export const OutboundCallFormDialogFields = ({
  draft,
  agents,
  onDraftChange,
  validationErrors = [],
  showSchedule = false,
  agentsLoading = false,
  excludeOutboundCallId
}: OutboundCallFormFieldsProps) => {
  const selectedAgent = agents.find((agent) => agent.id === draft.voiceAgentId) ?? agents[0];
  const callbackPhonePlaceholder = resolveVoiceAgentCallbackPhonePlaceholder(selectedAgent);
  return (
    <Fieldset className="col-span-2">
      <FieldGroup>
        <CustomerFields
          draft={draft}
          onDraftChange={onDraftChange}
          validationErrors={validationErrors}
          excludeOutboundCallId={excludeOutboundCallId}
        />
      </FieldGroup>
      <FieldGroup>
        <MessageFields
          draft={draft}
          onDraftChange={onDraftChange}
          compact={false}
          validationErrors={validationErrors}
          callbackPhonePlaceholder={callbackPhonePlaceholder}
        />
      </FieldGroup>
      <FieldGroup>
        <AgentSelect
          draft={draft}
          agents={agents}
          onDraftChange={onDraftChange}
          agentsLoading={agentsLoading}
          allowEmpty={false}
          validationErrors={validationErrors}
        />
        {showSchedule && (
          <ScheduleFields
            draft={draft}
            onDraftChange={onDraftChange}
            validationErrors={validationErrors}
          />
        )}
        <VoicemailSwitch draft={draft} onDraftChange={onDraftChange} />
      </FieldGroup>
    </Fieldset>
  );
};
